// LeaveAccountBlock — the self-leave affordance (ACL section 3.2), shown as the
// LAST block of the Profile & security section's Profile TAB (ruling R2,
// operator feedback F17; placement per the Task 8 amendment).
//
// ⚠️ AMENDED 2026-10-02 (account-area redesign, Task 4). ORIGINAL: a global "Membership" card
// rendered under EVERY section by AccountCenter, with an inline "Leave this account" /
// "Leave Account" / "Cancel" confirmation, and a sentence telling owners they cannot leave.
// CURRENT: this block, rendered only inside Profile & security for non-owners (an owner can
// never leave, so the sentence is gone), confirming in a modal alertdialog. Why: a permanent
// leave reminder under every page was the operator's complaint; Profile is ungated, so every
// member can still reach it even when their role lacks manage-membership.
//
// ⚠️ AMENDED 2026-10-04 (account-area redesign, Task 8, F17/F21). "Only inside
// Profile & security" (above) became "as the leaveSlot of that section's
// Profile TAB": ProfilePanel renders the section's kinds of information as
// tabs, and the shell hands this block down as the panel's leaveSlot prop —
// mounted only while the Profile tab is active and always that pane's LAST
// element child. This file's own markup (borderSubtle divider, h3, danger
// size="sm" trigger, the alertdialog) is UNCHANGED; the shell's leave-done
// banner moved nowhere with it.
//
// The leave logic itself is unchanged from AccountCenter.handleConfirmLeave: the server's
// refusal message (for example the last-owner rule) is shown VERBATIM, and after success the
// caller's self-view is re-read because their acting account and role changed.
import { useCallback, useState } from "react";
import {
  useEmbedTokens, EmbedBanner, EmbedButton, EmbedDialog, useEmbedDialog,
} from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import { leaveMembership, type SelfViewWire } from "./membership-api";

/**
 * Today's global-card condition (a live membership row) narrowed by ruling R2 to non-owners:
 * a solo owner has nothing to leave, and an owner with a row would only get the server's
 * transfer-first refusal.
 */
export function shouldShowLeave(sv: SelfViewWire | null): boolean {
  return sv?.membership != null && sv.role !== "owner";
}

export interface LeaveAccountBlockProps {
  accountLabel: string | null;
  /** Fired with the success message once the server accepted the leave. */
  onLeft: (message: string) => void;
  reloadSelfView: () => Promise<void>;
}

export function LeaveAccountBlock({ accountLabel, onLeft, reloadSelfView }: LeaveAccountBlockProps) {
  const t = useEmbedTokens();
  const dialog = useEmbedDialog();
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = accountLabel ?? "this account";

  const confirm = useCallback(async () => {
    setLeaving(true);
    setError(null);
    try {
      await leaveMembership();
      dialog.close();
      onLeft(`You have left ${name}.`);
      // Re-seed from the source of truth: the caller's acting account and role changed
      // (they may now act in their own legacy row, or in no account at all).
      await reloadSelfView();
    } catch (err) {
      // The last-owner refusal surfaces VERBATIM: the server message is the contract.
      setError(err instanceof ApiError ? err.message : "Failed to leave this account.");
    } finally {
      setLeaving(false);
    }
  }, [dialog, name, onLeft, reloadSelfView]);

  return (
    <section
      aria-labelledby="leave-account-heading"
      style={{
        marginTop: t.spacing.xl, paddingTop: t.spacing.lg, borderTop: `1px solid ${t.colors.borderSubtle}`,
        fontFamily: t.fontFamily,
      }}
    >
      <h3
        id="leave-account-heading"
        style={{ margin: 0, fontSize: t.fontSize.md, fontWeight: t.fontWeight.medium, color: t.colors.text }}
      >
        Leave this account
      </h3>
      <p style={{ margin: `${t.spacing.xs}px 0 ${t.spacing.md}px`, fontSize: t.fontSize.sm, color: t.colors.textSecondary }}>
        You are a member of {name} and can leave at any time.
      </p>
      <EmbedButton
        type="button" variant="danger" size="sm"
        onClick={(e) => { setError(null); dialog.openFrom(e.currentTarget); }}
      >
        Leave this account
      </EmbedButton>

      <EmbedDialog
        open={dialog.open}
        onClose={dialog.close}
        returnFocusTo={dialog.returnFocusTo}
        role="alertdialog"
        size="small"
        title={`Leave ${name}?`}
        closeDisabled={leaving}
        footer={
          <>
            <EmbedButton type="button" variant="secondary" onClick={dialog.close} disabled={leaving}>Cancel</EmbedButton>
            <EmbedButton type="button" variant="danger" onClick={confirm} loading={leaving}>Leave account</EmbedButton>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: t.fontSize.sm, color: t.colors.text }}>
          You will lose access to this account&apos;s orders, cart and settings.
        </p>
        {error && (
          <div style={{ marginTop: t.spacing.md }}>
            <EmbedBanner tone="error">{error}</EmbedBanner>
          </div>
        )}
      </EmbedDialog>
    </section>
  );
}
